import { useCallback, useEffect, useState } from 'react'

// Controls the HubSpot Conversations widget (loaded by the ui-seeker HubSpotChat plugin)
// from the sidebar. `window.HubSpotConversations` is a global object, so the front can drive
// it without touching ui-seeker.
//
// Open/closed state is derived from the `hs-messages-widget-open` class that HubSpot toggles
// on <html> (its own source of truth — verified against the real prod DOM), observed with a
// MutationObserver. This avoids depending on undocumented open/close event names and stays
// correct whether the panel is toggled from our icon or closed with HubSpot's own X.
// The floating launcher is suppressed and the panel is re-anchored via CSS (_hubspot.scss),
// keyed off that same class. The unread badge comes from `unreadConversationCountChanged`.

const OPEN_CLASS = 'hs-messages-widget-open'

const getApi = () => (typeof window !== 'undefined' ? window.HubSpotConversations : null)

const onReady = cb => {
    if (getApi()) return cb()
    window.hsConversationsOnReady = window.hsConversationsOnReady || []
    window.hsConversationsOnReady.push(cb)
}

const isWidgetOpen = () =>
    typeof document !== 'undefined' && document.documentElement.classList.contains(OPEN_CLASS)

export default function useHubSpotChat() {
    const [isOpen, setIsOpen] = useState(isWidgetOpen())
    const [unreadCount, setUnreadCount] = useState(0)

    // Track open/closed via the class HubSpot sets on <html>.
    useEffect(() => {
        const sync = () => setIsOpen(isWidgetOpen())
        sync()
        const observer = new MutationObserver(sync)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    // Unread count for the badge.
    useEffect(() => {
        const handleUnread = payload => setUnreadCount(payload?.unreadCount || 0)
        const register = () => {
            const api = getApi()
            if (api) api.on('unreadConversationCountChanged', handleUnread)
        }
        onReady(register)
        return () => {
            const api = getApi()
            if (api) api.off('unreadConversationCountChanged', handleUnread)
        }
    }, [])

    const toggle = useCallback(() => {
        onReady(() => {
            const api = getApi()
            if (!api) return
            if (isWidgetOpen()) api.widget.close()
            else api.widget.open()
        })
    }, [])

    return { isOpen, unreadCount, toggle }
}
